import { Configuration, LogLevel, NavigationClient, NavigationOptions } from '@azure/msal-browser';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';

const clientId = process.env.REACT_APP_AZURE_CLIENT_ID || '';
const authority = process.env.REACT_APP_AZURE_AUTHORITY || `https://login.microsoftonline.com/${process.env.REACT_APP_AZURE_TENANT_ID || 'common'}`;
// Use current origin for redirect URI to support multiple domains (apps.surbhi.net, emd.surbhi.net, etc.)
const defaultWebRedirectUri = typeof window !== 'undefined' 
  ? `${window.location.origin}/auth-callback`
  : 'https://apps.surbhi.net/auth-callback';
const webRedirectUri = process.env.REACT_APP_REDIRECT_URI || defaultWebRedirectUri;
export const nativeRedirectUri = process.env.REACT_APP_NATIVE_REDIRECT_URI || 'com.sepl.emp://auth';
const redirectUri = Capacitor.isNativePlatform() ? nativeRedirectUri : webRedirectUri;
export const nativeAuthResponseStorageKey = 'sepl.nativeAuthRedirectResponse';

// The absolute URL of the web-side auth-callback page.
// Used to navigate back to our app after the native auth browser closes.
export const webCallbackUrl = webRedirectUri;

/**
 * Custom MSAL navigation client for Capacitor/Android.
 *
 * The default MSAL behaviour calls window.location.assign() which navigates
 * the Capacitor WebView to Microsoft's login page, destroying our React app
 * context, wiping sessionStorage (MSAL's PKCE state), and preventing the
 * appUrlOpen callback from reaching our code.
 *
 * This override instead opens the auth URL in a Chrome Custom Tab via the
 * Capacitor Browser plugin, leaving our WebView (and its localStorage/cache)
 * completely intact. When Microsoft redirects to com.sepl.emp://auth?code=...
 * Android closes the Custom Tab and fires appUrlOpen in our still-running
 * WebView.
 */
class CapacitorNavigationClient extends NavigationClient {
  async navigateExternal(url: string, _options: NavigationOptions): Promise<boolean> {
    await Browser.open({ url });
    return true; // tell MSAL we handled the navigation
  }
}

// Use localStorage for MSAL state persistence to survive browser redirects,
// tab switches, and page reloads. On Capacitor, WebView can be killed during
// Microsoft login, destroying sessionStorage. localStorage survives this.
// On web browsers, localStorage persists across redirects more reliably than sessionStorage.
const cacheLocation = 'localStorage';
const navigationClient = Capacitor.isNativePlatform()
  ? new CapacitorNavigationClient()
  : new NavigationClient();

export const msalConfig: Configuration = {
  auth: {
    clientId,
    authority,
    redirectUri,
    postLogoutRedirectUri: redirectUri,
    navigateToLoginRequestUrl: false,
  },
  cache: {
    cacheLocation,
    // Keep temporary auth transaction state in localStorage to survive browser
    // process swaps and strict callback reloads on web.
    temporaryCacheLocation: 'localStorage',
    // Cookie backup avoids occasional storage loss in some browser contexts.
    storeAuthStateInCookie: true,
  },
  system: {
    allowNativeBroker: false,
    // Always provide a navigation client. Web must use the default client,
    // and native uses Browser.open() to keep the WebView alive.
    navigationClient,
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) return;
        // Log all MSAL messages on native so they surface in adb logcat / WebView console
        if (Capacitor.isNativePlatform()) {
          if (level === LogLevel.Error) {
            console.error('[MSAL]', message);
          } else if (level === LogLevel.Warning) {
            console.warn('[MSAL]', message);
          } else {
            console.log('[MSAL]', message);
          }
          return;
        }
        // On web, log warnings and errors to help debug MSAL issues
        if (level === LogLevel.Error) {
          console.error('[MSAL]', message);
        }
        if (level === LogLevel.Warning) {
          console.warn('[MSAL]', message);
        }
      },
      piiLoggingEnabled: false,
      logLevel: LogLevel.Warning,  // Always show warnings on web to catch cache issues
    },
  },
};

export const loginRequest = {
  scopes: ['User.Read'],
};
